import posthog from 'posthog-js';
import {
  decodeJwtPayload,
  IMPERSONATION_EXPIRED_EVENT,
  TOKENS_UPDATED_EVENT,
} from '@/lib/api/client';
import { APP_ENVIRONMENT } from '@/lib/env';
import { workspaceIdFromPath } from '@/utils/workspacePath';

// Cached super-props set from the active access token. trackEvent is called
// often (per page view, per workspace open, per generation); re-decoding the
// JWT on every call is wasteful.
let cachedImpersonationProps: { is_impersonated: true; impersonated_by: string } | null = null;
let cachedForToken: string | null = null;

function getImpersonationProps() {
  const token = localStorage.getItem('pd_access_token') || '';
  if (token === cachedForToken) return cachedImpersonationProps;
  cachedForToken = token;
  if (!token) {
    cachedImpersonationProps = null;
    return null;
  }
  const impersonatedBy = decodeJwtPayload(token)?.impersonated_by;
  cachedImpersonationProps =
    typeof impersonatedBy === 'string' && impersonatedBy
      ? { is_impersonated: true, impersonated_by: impersonatedBy }
      : null;
  return cachedImpersonationProps;
}

if (typeof window !== 'undefined') {
  const invalidate = () => {
    cachedForToken = null;
    cachedImpersonationProps = null;
  };
  window.addEventListener(TOKENS_UPDATED_EVENT, invalidate);
  window.addEventListener(IMPERSONATION_EXPIRED_EVENT, invalidate);
  window.addEventListener('storage', (e) => {
    if (e.key === 'pd_access_token') invalidate();
  });
}

// Owned readiness flag — flipped true after a successful `posthog.init()`.
// We don't read posthog-js's internal `__loaded` field because it's a
// private SDK detail and could disappear or be renamed across upgrades,
// silently making every flag fall through to its default forever.
let posthogInitialized = false;

export function initAnalytics() {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;
  if (!apiKey) {
    return;
  }

  // Feature flags are now fetched from the Imagen backend (GET
  // /api/feature-flags) — disable the SDK's own /decide calls entirely so
  // ad-blockers can't disable flags and clients don't double-fetch.
  posthog.init(apiKey, {
    api_host: apiHost,
    capture_pageview: 'history_change',
    advanced_disable_feature_flags: true,
  });
  posthog.register({
    app_version: __APP_VERSION__.sha,
    app_tag: __APP_VERSION__.tag,
    // The Imagen backend serves both posedraw and chiqlook into one PostHog
    // project, so every server event carries `app` (the type in
    // `imagen/src/libs/posthog.ts` enforces it). Client events had none, which
    // meant no insight could filter client and server events the same way —
    // any `app`-filtered query silently dropped the entire client side.
    app: 'posedraw',
  });
  posthogInitialized = true;
}

// Must match useDeviceLayout: LARGE is >1024
const MEDIUM_SCREEN_MAX_WIDTH = 1024;

function getWorkspaceIdFromUrl(): string | undefined {
  return workspaceIdFromPath(window.location.pathname);
}

function getLayout(): 'desktop' | 'mobile' {
  return window.innerWidth > MEDIUM_SCREEN_MAX_WIDTH ? 'desktop' : 'mobile';
}


/** Standardized source vocabulary for analytics events — represents the feature/screen. */
export type AnalyticsSource = 'generator' | 'versions' | 'character' | 'camera' | 'setup' | 'editor' | 'gallery';

/**
 * Where a download/export was initiated from — the `source` on
 * `clicked_download_image`.
 *
 * Kept as a closed union rather than a bare `string` because
 * `downloadOrShareImage` takes `source` positionally, immediately before the
 * optional `filename` it displaced. With `string` the pre-refactor call shape
 * `downloadOrShareImage(url, 'character-sheet.png')` still compiles — it would
 * silently file every download under a source named after a filename. A typo
 * would likewise fork a funnel segment in silence, and this event is the
 * terminal step of the activation funnel.
 */
export type DownloadSource =
  | 'generator'
  | 'versions'
  | 'gallery'
  | 'gallery_viewer'
  | 'character_sheet_create'
  | 'character_sheet_detail'
  | 'character_sheet_viewer'
  | 'character_sheet_mobile'
  | 'character_series_mobile'
  | 'model_reference'
  | 'versions_panel';

// App-layer kill switch for trackEvent, independent of posthog-js's internal
// opt-out state. useAuth sets this true *before* asking the SDK to opt out
// during impersonation, so even if `posthog.opt_out_capturing()` silently
// fails, high-volume app-routed events are still blocked.
// identifyAnalyticsUser intentionally does NOT honor this flag — see its
// comment for the reasoning.
let captureSuppressed = false;

export function suppressCapture(): void {
  captureSuppressed = true;
}

export function unsuppressCapture(): void {
  captureSuppressed = false;
}

export function isCaptureSuppressed(): boolean {
  return captureSuppressed;
}

export function pauseAnalyticsCapture(): void {
  if (!posthogInitialized) return;
  try {
    posthog.opt_out_capturing();
  } catch (err) {
    // Privacy control — surface the failure loudly. useAuth re-checks via
    // isAnalyticsCapturePaused() and falls back to suppressed-anonymous mode.
    console.error('[analytics] pauseAnalyticsCapture failed', err);
  }
}

export function resumeAnalyticsCapture(): void {
  if (!posthogInitialized) return;
  try {
    posthog.opt_in_capturing();
  } catch (err) {
    console.error('[analytics] resumeAnalyticsCapture failed', err);
  }
}

export function isAnalyticsCapturePaused(): boolean {
  if (!posthogInitialized) return false;
  try {
    return posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
}

/**
 * Capture an event. Fire-and-forget, and **never throws** — telemetry must not
 * change what the app does.
 *
 * That guarantee is load-bearing now that events are emitted from inside
 * operations rather than from click handlers: `downloadOrShareImage` captures
 * on its success path and inside its `catch`, so a throwing capture there would
 * turn a completed download into a "Download failed" toast, or make a cancelled
 * share reject instead of returning cleanly. Mirrors the same contract on the
 * server (`imagen/src/libs/posthog.ts`).
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  if (!posthogInitialized) return;
  if (captureSuppressed) return;
  try {
    const workspace_id = getWorkspaceIdFromUrl();
    posthog.capture(event, {
      ...properties,
      ...(workspace_id ? { workspace_id } : {}),
      ...getImpersonationProps(),
      layout: getLayout(),
      environment: APP_ENVIRONMENT,
    });
  } catch (err) {
    // Swallow, but don't hide it — a capture that silently stops working looks
    // exactly like a feature nobody uses.
    console.error('[analytics] trackEvent failed', event, err);
  }
}

export function identifyAnalyticsUser(userId: string) {
  if (!posthogInitialized) return;
  // NOT gated by captureSuppressed: identify must rebind the local
  // distinct_id during impersonation so /decide returns the target user's
  // flag variants. useAuth verifies posthog.has_opted_out_capturing()
  // before calling, so the SDK opt-out is what actually blocks the
  // $identify network event. Don't re-add a suppression check here.
  posthog.identify(userId);
}

export function resetAnalyticsUser() {
  if (!posthogInitialized) return;
  // reset() does not emit a capture event; safe to call regardless of
  // suppression. Used by the useAuth fallback when SDK pause verification
  // fails, to drop any prior identified state so even unblocked SDK
  // pageviews fire against an anonymous distinctId instead of the target.
  posthog.reset();
}

